/**
 * src/components/shared/VoiceFileUploadBtn.jsx — Compact voice file upload button
 * For uploading large audio files (.m4a/.mp3/.wav) from Pipeline, Projects, etc.
 * Supports contextType-aware classification and progress tracking.
 */

import { useState, useRef, useCallback, useContext } from 'react';
import { Upload, Loader2, CheckCircle, AlertCircle, X } from 'lucide-react';
import { useT } from '../../lib/i18n/I18nContext';
import { AppDataContext } from '../../lib/AppDataContext';
import useCaptureRouter from '../../lib/capture/useCaptureRouter';
import { ModalSheet } from '../SharedUI';
import UploadFilesPanel from '../capture/UploadFilesPanel';

function lookupScopeLabel(contextType, contextId, data) {
  if (!contextId || !data) return null;
  if (contextType === 'project') {
    return data.projects?.find((p) => p.id === contextId)?.project_name || null;
  }
  if (contextType === 'task') {
    return data.tasks?.find((t) => t.id === contextId)?.task_name || null;
  }
  if (contextType === 'deal') {
    return data.deals?.find((d) => d.id === contextId)?.deal_name || null;
  }
  if (contextType === 'bundle') {
    return data.bundles?.find((b) => b.id === contextId)?.bundle_name || null;
  }
  return null;
}

const pulseKeyframes = `
@keyframes voiceUploadPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}
`;

export function ProgressCard({ upload, onDismiss }) {
  const { fileName, status, chunksDone, chunksTotal, headline, error, pct } = upload;

  const label =
    status === 'uploading' ? `Uploading ${fileName}...${typeof pct === 'number' ? ` ${pct}%` : ''}` :
    status === 'processing' ? `Transcribing... ${chunksDone || 0}/${chunksTotal || '?'}` :
    status === 'classifying' ? 'Classifying...' :
    status === 'ready' || status === 'classified' ? `✓ ${headline || 'Done'}` :
    status === 'error' ? (error || 'Upload failed') :
    `${status}...`;

  const isActive = !['ready', 'classified', 'error'].includes(status);
  const isError = status === 'error';
  const isDone = status === 'ready' || status === 'classified';

  return (
    <div
      onClick={onDismiss} role="button" tabIndex={0} onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onDismiss?.(e); } }}
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '6px 10px', borderRadius: 6, fontSize: 11,
        background: isError ? 'rgba(220,38,38,0.1)' : isDone ? 'rgba(34,197,94,0.1)' : 'var(--glass)',
        color: isError ? 'var(--bad)' : isDone ? 'var(--ok)' : 'var(--text-secondary)',
        cursor: 'pointer',
        animation: isActive ? 'voiceUploadPulse 1.5s ease-in-out infinite' : 'none',
        maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}
    >
      {isActive && <Loader2 size={10} className="animate-spin" />}
      {isDone && <CheckCircle size={10} />}
      {isError && <AlertCircle size={10} />}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
      <X size={10} style={{ flexShrink: 0, opacity: 0.5 }} />
    </div>
  );
}

export default function VoiceFileUploadBtn({ contextType = 'project', contextId, onUpload }) {
  const { t } = useT();
  // Null-safe context read so isolation tests don't need to wrap in
  // AppDataProvider just to render the button.
  const data = useContext(AppDataContext);
  const scopeLabelStr = lookupScopeLabel(contextType, contextId, data);
  const [dragOver, setDragOver] = useState(false);
  const [dropSaveFirstMsg, setDropSaveFirstMsg] = useState(null);
  const fileRef = useRef(null);
  // The routing (image -> photos, audio -> UVTP, text -> note, docs -> vault) lives in
  // useCaptureRouter so the chat attach button files through the same routes.
  const { uploading, uploads, dismissUpload, processFiles, vaultQueue, vaultContext, closeVault } =
    useCaptureRouter({ contextType, contextId, onUpload });

  const handleFile = async (e) => {
    await processFiles(e.target.files || []);
    // Reset the input so picking the same file again triggers onChange
    if (fileRef.current) fileRef.current.value = '';
    if (e.target) e.target.value = '';
  };

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setDragOver(false);
    if (!e.dataTransfer.files || e.dataTransfer.files.length === 0) {
      const hasUriItem = Array.from(e.dataTransfer.items || []).some(
        item => item.type === 'text/uri-list' || item.type === 'text/html'
      );
      if (hasUriItem) {
        setDropSaveFirstMsg(t('forms.dropWhatsAppSaveFirst'));
        setTimeout(() => setDropSaveFirstMsg(null), 5000);
      }
      return;
    }
    await processFiles(e.dataTransfer.files);
  }, [processFiles, t]);

  return (
    <>
      <style>{pulseKeyframes}</style>
      <div
        data-testid="voice-upload-drop-zone"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        style={{
          display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start',
          border: dragOver ? '2px dashed var(--accent)' : '2px solid transparent',
          borderRadius: 10, padding: dragOver ? 4 : 0,
          transition: 'all 150ms ease',
        }}
      >
        {dropSaveFirstMsg && (
          <div data-testid="voice-drop-save-first" style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--glass)', color: 'var(--t2)', fontSize: 12 }}>
            {dropSaveFirstMsg}
          </div>
        )}
        <button
          data-testid="voice-upload-btn"
          onClick={() => fileRef.current?.click()}
          disabled={uploading || !contextId}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '8px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600,
            background: 'var(--glass)', color: 'var(--accent)',
            border: 'none', cursor: uploading ? 'default' : 'pointer',
            opacity: uploading ? 0.6 : 1, minHeight: 44, whiteSpace: 'nowrap',
          }}
        >
          {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
          {uploading ? t('forms.uploading') : t('forms.uploadFile')}
        </button>

        {uploads.map(u => (
          <ProgressCard
            key={u.uploadId}
            upload={u}
            onDismiss={() => dismissUpload(u.uploadId)}
          />
        ))}
      </div>

      {/*
        * iOS Safari Files picker greys out audio files with stripped UTIs
        * (e.g. third-party voice recorders exporting 'AUDIO-2026-03-...'
        * with no extension). Even broad audio/star,.m4a,... filters exclude
        * them. Falling back to star/star (any file) lets the user select;
        * handleFile (size + isAcceptableAudioFile + ext check) and the
        * Whisper backend are the real gates. CEO bug 2026-05-03 — three
        * iterations to track this down across all upload entry points.
        */}
      <input
        ref={fileRef}
        type="file"
        accept="*/*"
        multiple
        aria-label={t('forms.uploadAudioAria')}
        style={{ display: 'none' }}
        onChange={handleFile}
      />

      {/* Document capture: same panel the Files tabs mount, pre-seeded with the
          dropped files and pre-scoped to this deal/project. The scope-confirm
          sheet inside it still gets the final say. */}
      {vaultQueue && (
        <ModalSheet open onClose={closeVault} title={t('vaultCapture.dropTitle')}>
          <div data-testid="voice-upload-vault-sheet">
            <UploadFilesPanel
              initialFiles={vaultQueue}
              context={vaultContext}
              onFiled={onUpload}
            />
          </div>
        </ModalSheet>
      )}
    </>
  );
}
